/**
 * CarrierSelect — searchable single-select dropdown for picking one Carrier.
 * Built on react-select (already used elsewhere in this app, e.g. the Vendor multi-select on the
 * Receiving Shipment screen), so type-to-filter search comes for free without a new dependency.
 *
 * Data source: GET api/Carrier/getall (CarrierService.getAllCarriers), fetched once and cached at
 * module scope for the life of the page.
 *
 * Pre-selection (edit mode): pass `value` as { id, name }. The selected option renders immediately
 * from that snapshot even before the carrier list finishes loading, and is reconciled against the
 * live list once it arrives (so a since-renamed/removed carrier still shows the name that was
 * saved with the record, rather than going blank).
 */
import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { CarrierService } from '../../../service/api/carrier-service';

let carrierListPromise = null;
function loadCarriersOnce() {
  if (!carrierListPromise) {
    carrierListPromise = CarrierService.getAllCarriers()
      .then((res) => {
        if (res?.status !== 200) {
          throw new Error(res?.errorMessage?.message || `unexpected status ${res?.status}`);
        }
        return Array.isArray(res.response) ? res.response : [];
      })
      .catch((err) => {
        carrierListPromise = null; // allow a retry on the next mount after a failure
        throw err;
      });
  }
  return carrierListPromise;
}

export default function CarrierSelect({
  value = null,
  onChange,
  placeholder = 'Search for a carrier…',
  isDisabled = false,
  isClearable = true,
  id,
}) {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    loadCarriersOnce()
      .then((list) => { if (alive) setCarriers(list); })
      .catch((err) => {
        console.error('CarrierSelect: failed to load Carrier/getall', err);
        if (alive) setError(err?.message ? `Could not load the carrier list (${err.message}).` : 'Could not load the carrier list.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const options = useMemo(
    () => carriers
      .map((c) => ({
        value: String(c.id ?? c.Id ?? ''),
        label: c.carrierName ?? c.CarrierName ?? c.carrierShortName ?? c.CarrierShortName ?? '',
        carrier: c,
      }))
      .filter((o) => o.value && o.label),
    [carriers],
  );

  const selectedOption = useMemo(() => {
    if (!value?.id) return null;
    const match = options.find((o) => o.value === String(value.id));
    return match || { value: String(value.id), label: value.name || String(value.id) };
  }, [value, options]);

  const handleChange = (option) => {
    onChange?.(option ? { id: option.value, name: option.label } : null);
  };

  return (
    <div>
      {/* react-select's internal CSS-in-JS doesn't take print overrides reliably (the selected
          value can end up rendered outside its box). Simplest robust fix: hide the interactive
          control on the printed page and show a plain text box with the same value instead —
          the same pattern as every other locked field on this form. */}
      <style>{`
        .cs-print-summary{display:none}
        @media print{
          .cs-interactive{display:none}
          .cs-print-summary{display:block;font:inherit;font-size:13px;color:inherit;
            border:1px solid #9AA5B4;border-radius:6px;padding:7px 9px;min-height:34px;
            white-space:pre-wrap;word-break:break-word;box-sizing:border-box}
        }
      `}</style>
      <div className="cs-interactive">
        <Select
          inputId={id}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isLoading={loading}
          options={options}
          value={selectedOption}
          onChange={handleChange}
          placeholder={placeholder}
          classNamePrefix="react-select"
          noOptionsMessage={() => (loading ? 'Loading carriers…' : error || 'No carriers found')}
        />
        {error && <div className="text-danger" style={{ fontSize: 11.5, marginTop: 4 }}>{error}</div>}
      </div>
      <div className="cs-print-summary">{selectedOption?.label || ' '}</div>
    </div>
  );
}
